import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Star, FileText, ArrowUpRight } from "lucide-react";
import portrait from "../assets/danyale-portrait.jpg";

const skills = [
  "Brand Identity",
  "Logo Design",
  "Art Direction",
  "Figma",
  "Photography",
  "Videography",
  "Sketching",
  "3D Design",
  "Editorial",
  "Type Design",
  "Motion",
  "Adobe Creative Suite",
];

const timeline = [
  { year: "Since 2020", title: "Photographer", place: "Freelance" },
  { year: "Since 2023", title: "Graphic Designer", place: "Freelance" },
  { year: "Since 2020", title: "Creative Director", place: "Freelance" },
  { year: "2024", title: "Content Coordinator", place: "Klik Marketing" },
  { year: "2022", title: "Assistant Videographer & Photographer", place: "Rae's Images" },
];

export default function About() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-10 py-6 md:py-10" data-testid="about-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
        {/* Left column */}
        <div className="lg:col-span-5 flex flex-col gap-6">
          <div className="bg-[#FEFBEA] border-4 border-black rounded-[2rem] p-2 shadow-[10px_10px_0_0_rgba(0,0,0,1)]">
            <div className="aspect-square rounded-[1.7rem] border-4 border-black overflow-hidden bg-pink-200">
              <img
                src={portrait}
                alt="Danyale portrait"
                className="w-full h-full object-cover"
                data-testid="about-portrait"
                draggable={false}
              />
            </div>
          </div>

          <div className="bg-pink-600 border-4 border-black rounded-2xl p-5 text-white shadow-[6px_6px_0_0_rgba(0,0,0,1)]">
            <div className="flex items-center gap-2 font-display text-2xl">
              <Star className="w-5 h-5" />
              currently_booking_2026
            </div>
            <p className="mt-2 font-body font-bold text-sm">
              Available for select branding & UX engagements.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full border-2 border-black bg-yellow-300 text-black font-body font-extrabold uppercase text-xs shadow-[3px_3px_0_0_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[1px_1px_0_0_rgba(0,0,0,1)] transition-all"
            >
              <Mail className="w-4 h-4" /> Get in touch
            </Link>
          </div>

          {/* Resume card */}
          <div className="bg-pink-600 border-4 border-black rounded-2xl p-5 text-white shadow-[6px_6px_0_0_rgba(0,0,0,1)]">
            <div className="flex items-center gap-2 font-display text-2xl">
              <FileText className="w-5 h-5" />
              view_resume
            </div>
            <p className="mt-2 font-body font-bold text-sm">
              A snapshot of my experience, skills, and projects.
            </p>
            <a
              href="/danyale-resume.pdf"
              target="_blank"
              rel="noreferrer"
              data-testid="about-cta-resume"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full border-2 border-black bg-yellow-300 text-black font-body font-extrabold uppercase text-xs shadow-[3px_3px_0_0_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[1px_1px_0_0_rgba(0,0,0,1)] transition-all"
            >
              <ArrowUpRight className="w-4 h-4" /> Open Resume
            </a>
          </div>
        </div>

        {/* Right column */}
        <div className="lg:col-span-7 flex flex-col gap-6">
          <div className="bg-[#FEFBEA] border-4 border-black rounded-[2rem] p-6 md:p-10 shadow-[10px_10px_0_0_rgba(0,0,0,1)]">
            <h1 className="font-display text-5xl md:text-7xl text-black tracking-wide">
              ABOUT <span className="text-pink-600">ME</span>
            </h1>
            <p className="mt-5 font-body font-bold text-black text-base md:text-lg leading-relaxed">
              Hi, I&apos;m Danyale. I help small brands feel bold, big brands feel
              human, and good ideas find their visual voice. With 5+ years of
              experience across photography, graphic design, videography, and
              content creation, my work blends bold aesthetics, editorial
              storytelling, and modern visual design to create work that feels
              expressive, intentional, and visually memorable.
            </p>
            <p className="mt-4 font-body font-semibold text-black/80 text-base leading-relaxed">
              I&apos;m a creative who loves turning ideas into visuals that
              connect people through art, emotion, and storytelling, creating
              experiences that not only look good, but leave a lasting impression.
            </p>

            <div className="mt-6 flex items-center gap-2 font-body font-extrabold text-black/80 text-sm">
              <MapPin className="w-4 h-4" /> Working worldwide
            </div>
          </div>

          {/* Skills */}
          <div className="bg-white border-4 border-black rounded-2xl p-5 md:p-6 shadow-[6px_6px_0_0_rgba(0,0,0,1)]">
            <h2 className="font-display text-3xl md:text-4xl text-black tracking-wide">
              SKILLS / <span className="text-pink-600">TOOLS</span>
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {skills.map((s) => (
                <span
                  key={s}
                  data-testid={`skill-${s.toLowerCase().replace(/\W/g, "-")}`}
                  className="px-3 py-1.5 rounded-full border-2 border-black bg-yellow-300 font-body font-extrabold text-xs uppercase shadow-[2px_2px_0_0_rgba(0,0,0,1)]"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-[#FEFBEA] border-4 border-black rounded-2xl p-5 md:p-6 shadow-[6px_6px_0_0_rgba(0,0,0,1)]">
            <h2 className="font-display text-3xl md:text-4xl text-black tracking-wide">
              EXPERI<span className="text-pink-600">ENCE</span>
            </h2>
            <ul className="mt-4 divide-y-2 divide-black/80">
              {timeline.map((t, i) => (
                <li
                  key={i}
                  className="py-3 flex items-baseline justify-between gap-4"
                  data-testid={`timeline-row-${i}`}
                >
                  <div>
                    <p className="font-body font-extrabold text-black uppercase">{t.title}</p>
                    <p className="font-body font-semibold text-black/70 text-sm">{t.place}</p>
                  </div>
                  <span className="font-display text-pink-600 text-xl whitespace-nowrap">
                    {t.year}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
